/**
 * The waterfall's fill order, as the operator sees and changes it (v7.0.0).
 *
 * PURE — no DOM. The server's rule (src/money/waterfall.ts) is: `priority`
 * DESC, then the operator's own screen order, then name. The envelope list
 * already arrives in screen order (sort_order, name), so a STABLE sort on
 * priority alone reproduces the server's order exactly. test/unit/fill-order
 * pins the two together, because a screen that shows one order while the
 * waterfall uses another would be a quiet lie about where the money goes.
 */

import type { ApiEnvelope } from './api';

/**
 * Which envelopes the waterfall can fill at all: a named envelope with a target.
 * `unallocated` is the source, never a destination, and an envelope with no
 * target has nothing to be filled TO — so neither can be put in an order.
 * (Completed envelopes are archived and never reach the list.)
 */
export function isOrderable(e: Pick<ApiEnvelope, 'type' | 'targetMinor'>): boolean {
  return e.type !== 'unallocated' && e.targetMinor != null;
}

/** The orderable envelopes, first-filled first. */
export function fillOrder(envelopes: readonly ApiEnvelope[]): ApiEnvelope[] {
  return envelopes
    .filter(isOrderable)
    .map((envelope, index) => ({ envelope, index }))
    .sort((a, b) => b.envelope.priority - a.envelope.priority || a.index - b.index)
    .map((x) => x.envelope);
}

/** Move one id a step earlier (-1) or later (+1). A move off either end is a no-op. */
export function moveInOrder(ids: readonly string[], id: string, step: -1 | 1): string[] {
  const from = ids.indexOf(id);
  const to = from + step;
  if (from < 0 || to < 0 || to >= ids.length) return [...ids];
  const next = [...ids];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

export function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}
